import { useLingui } from "@lingui/react/macro";
import {
  AlertTriangle,
  ExternalLink,
  KeyRound,
  Receipt,
  RotateCcw,
  ShieldQuestion,
  ShieldX,
  Undo2,
  Wallet,
} from "lucide-react";
import type * as React from "react";
import { cn } from "../../lib/cn";
import { Button } from "../../primitives/button";

/**
 * action-lifecycle — DRY, presentational building blocks for the SOTA HITL
 * action-card lifecycle (confirm → execute → result). One place owns the
 * chrome so EVERY card surface (``ActionProposalSurface`` / the focused
 * overlay) reads identically:
 *
 *   - ``ActionKeyboardHints`` — the tiny ``⌘↵ Conferma · Esc Rifiuta`` affordance.
 *   - ``ActionUndoBar`` — the Gmail-style "Annulla invio" + calm countdown
 *     shown DURING the client-side pre-execute delay (Feature 2). The actual
 *     defer/cancel timer lives in ``useConfirmWithUndo`` (apps layer) — this is
 *     pure paint.
 *   - ``ActionReceipt`` — the post-execute deep-link row (Feature 3).
 *   - ``ActionErrorRecovery`` — the inline, cause-mapped recovery (Feature 4).
 *
 * Brand: cream/paper, hairline borders, ONE orange accent (links + primary
 * CTA), NO filled orange wash; typography-first, ``tabular-nums`` on every
 * number/countdown; ``prefers-reduced-motion`` respected (no spinny bar — a
 * calm width step).
 */

// ---------------------------------------------------------------------------
// Error-kind — LOCAL union (packages/ui deliberately does not depend on
// @ando/core). The apps layer maps the wire ``ActionErrorKind`` onto this.
// ---------------------------------------------------------------------------

export type ActionErrorKind =
  | "auth_expired"
  | "credit_exhausted"
  | "transient"
  | "validation"
  | "source_unverified"
  | "source_contradicted"
  | "generic";

// ---------------------------------------------------------------------------
// ActionKeyboardHints — subtle hint under the decision bar. aria-hidden: the
// buttons themselves keep their own aria-labels, so this is decoration.
// ---------------------------------------------------------------------------

export interface ActionKeyboardHintsProps {
  readonly className?: string;
  /** Hide the "Esc Rifiuta" half when the card has no reject affordance. */
  readonly canReject?: boolean;
}

const KBD_CLASS =
  "rounded border border-hairline bg-ink-1/[0.03] px-1 py-px font-mono text-[10px] text-ink-3 tabular-nums";

export const ActionKeyboardHints: React.FC<ActionKeyboardHintsProps> = ({
  className,
  canReject = true,
}) => {
  const { t } = useLingui();
  return (
    <p aria-hidden className={cn("flex items-center gap-1.5 text-[10.5px] text-ink-4", className)}>
      <kbd className={KBD_CLASS}>⌘↵</kbd>
      <span>{t`Conferma`}</span>
      {canReject ? (
        <>
          <span className="text-ink-4/70">·</span>
          <kbd className={KBD_CLASS}>Esc</kbd>
          <span>{t`Rifiuta`}</span>
        </>
      ) : null}
    </p>
  );
};
ActionKeyboardHints.displayName = "ActionKeyboardHints";

// ---------------------------------------------------------------------------
// ActionUndoBar — the "sto per inviare, puoi ancora annullare" affordance.
// ---------------------------------------------------------------------------

export interface ActionUndoBarProps {
  /** Whole seconds remaining before the deferred execute fires. */
  readonly secondsLeft: number;
  /** Total window length in seconds — drives the progress line. */
  readonly totalSeconds: number;
  /** Cancel the pending send (execute NEVER fires — nothing was sent). */
  readonly onCancel: () => void;
  /** Verb for the countdown, e.g. "Invio". Defaults to a generic "Esecuzione". */
  readonly actionNoun?: string;
  readonly className?: string;
}

export const ActionUndoBar: React.FC<ActionUndoBarProps> = ({
  secondsLeft,
  totalSeconds,
  onCancel,
  actionNoun,
  className,
}) => {
  const { t } = useLingui();
  const noun = actionNoun ?? t`Esecuzione`;
  const pct = totalSeconds > 0 ? Math.max(0, Math.min(100, (secondsLeft / totalSeconds) * 100)) : 0;
  return (
    <div className={cn("flex items-center gap-2", className)} aria-live="polite">
      <span className="text-[12px] text-ink-2">
        {/* tabular-nums keeps the countdown from shifting width each tick. */}
        {noun} {t`tra`} <span className="font-medium text-ink-1 tabular-nums">{secondsLeft}s</span>
      </span>
      {/* Calm progress line — a width STEP, not an animation. motion-reduce
          drops the transition entirely (styles.css also neutralises it). */}
      <span
        aria-hidden
        className="hidden h-[3px] w-24 overflow-hidden rounded-full bg-ink-1/[0.06] sm:block"
      >
        <span
          className="block h-full rounded-full bg-ando-orange/70 transition-[width] duration-300 ease-linear motion-reduce:transition-none"
          style={{ width: `${pct}%` }}
        />
      </span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onCancel}
        aria-label={t`Annulla invio`}
      >
        <Undo2 className="h-3 w-3" aria-hidden strokeWidth={1.75} />
        {t`Annulla`}
      </Button>
    </div>
  );
};
ActionUndoBar.displayName = "ActionUndoBar";

// ---------------------------------------------------------------------------
// ActionReceipt — post-execute deep link. Reused across every card type.
// ---------------------------------------------------------------------------

export interface ActionReceiptProps {
  /** Deep link to the created/updated artefact (opens in a new tab). */
  readonly url?: string | null;
  /** Human descriptor of the artefact / provider ("Opportunity", "Fattura"). */
  readonly label?: string | null;
  /** Provider record / message / transmission id — shown when no URL. */
  readonly externalId?: string | null;
  readonly className?: string;
}

export const ActionReceipt: React.FC<ActionReceiptProps> = ({
  url,
  label,
  externalId,
  className,
}) => {
  const { t } = useLingui();
  // Nothing to surface → render nothing (never an empty receipt row).
  if (!url && !externalId) return null;
  const openLabel = label ? t`Apri in ${label}` : t`Apri il risultato`;
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-2 gap-y-1 border-hairline border-t bg-white/40 px-3 py-2 text-[12px]",
        className,
      )}
    >
      <Receipt className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden strokeWidth={1.75} />
      <span className="text-ink-3">{t`Ricevuta`}</span>
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex items-center gap-1 rounded font-medium text-ando-orange hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45"
        >
          {openLabel}
          <ExternalLink className="h-3 w-3" aria-hidden strokeWidth={2} />
        </a>
      ) : null}
      {!url && externalId ? (
        <span className="inline-flex min-w-0 items-center gap-1.5 text-ink-2">
          {label ? <span className="text-ink-3">{label}</span> : null}
          <code className="truncate rounded bg-ink-1/[0.04] px-1 py-0.5 font-mono text-[11px] text-ink-2 tabular-nums">
            {externalId}
          </code>
        </span>
      ) : null}
    </div>
  );
};
ActionReceipt.displayName = "ActionReceipt";

// ---------------------------------------------------------------------------
// ActionErrorRecovery — inline, cause-mapped recovery. ONE component driven
// by the error-kind enum (DRY across every card type).
// ---------------------------------------------------------------------------

export interface ActionErrorRecoveryProps {
  readonly kind: ActionErrorKind;
  /** Raw BE message — shown as the secondary detail line. */
  readonly message?: string | null;
  /** Account/provider label for the "Riconnetti {account}" CTA. */
  readonly accountLabel?: string | null;
  /** Re-fire the execute mutation (transient / generic). */
  readonly onRetry?: () => void;
  /** Open the connector reconnect flow (auth_expired). */
  readonly onReconnect?: () => void;
  /** Open the credit top-up flow (credit_exhausted); falls back to onReconnect. */
  readonly onTopUpCredit?: () => void;
  /**
   * Re-execute confirming the value against the source of record
   * (source_unverified) — re-fires execute with ``confirm_source: true``.
   */
  readonly onConfirmSource?: () => void;
  /** System-of-record value(s) the gate found (source_unverified/contradicted). */
  readonly sourceValues?: readonly string[] | null;
  /** Disable CTAs while a retry is already in flight. */
  readonly busy?: boolean;
  readonly className?: string;
}

const KIND_ICON: Record<
  ActionErrorKind,
  React.ComponentType<{ className?: string; strokeWidth?: number }>
> = {
  auth_expired: KeyRound,
  credit_exhausted: Wallet,
  transient: RotateCcw,
  validation: AlertTriangle,
  source_unverified: ShieldQuestion,
  source_contradicted: ShieldX,
  generic: AlertTriangle,
};

export const ActionErrorRecovery: React.FC<ActionErrorRecoveryProps> = ({
  kind,
  message,
  accountLabel,
  onRetry,
  onReconnect,
  onTopUpCredit,
  onConfirmSource,
  sourceValues,
  busy = false,
  className,
}) => {
  const { t } = useLingui();
  const Icon = KIND_ICON[kind];

  const title =
    kind === "auth_expired"
      ? t`Connessione scaduta`
      : kind === "credit_exhausted"
        ? t`Credito esaurito`
        : kind === "validation"
          ? t`Controlla i campi`
          : kind === "transient"
            ? t`Problema temporaneo`
            : kind === "source_unverified"
              ? t`Da confermare con la fonte`
              : kind === "source_contradicted"
                ? t`In conflitto con i tuoi dati`
                : t`Esecuzione non riuscita`;

  // Primary CTA per cause. Validation has none — the user must fix a field
  // (the field errors already render inline next to the inputs).
  let cta: React.ReactNode = null;
  if (kind === "auth_expired" && onReconnect) {
    const relabel = accountLabel ? t`Riconnetti ${accountLabel}` : t`Riconnetti l'account`;
    cta = (
      <Button
        type="button"
        variant="primary"
        size="sm"
        onClick={onReconnect}
        disabled={busy}
        aria-label={relabel}
      >
        <KeyRound className="h-3 w-3" aria-hidden strokeWidth={2} />
        {relabel}
      </Button>
    );
  } else if (kind === "credit_exhausted" && (onTopUpCredit || onReconnect)) {
    cta = (
      <Button
        type="button"
        variant="primary"
        size="sm"
        onClick={onTopUpCredit ?? onReconnect}
        disabled={busy}
        aria-label={t`Ricarica credito`}
      >
        <Wallet className="h-3 w-3" aria-hidden strokeWidth={2} />
        {t`Ricarica credito`}
      </Button>
    );
  } else if ((kind === "transient" || kind === "generic") && onRetry) {
    cta = (
      <Button
        type="button"
        variant="primary"
        size="sm"
        onClick={onRetry}
        disabled={busy}
        aria-busy={busy || undefined}
        aria-label={t`Riprova`}
      >
        <RotateCcw
          className={cn("h-3 w-3", busy && "animate-spin motion-reduce:animate-none")}
          aria-hidden
          strokeWidth={2}
        />
        {t`Riprova`}
      </Button>
    );
  } else if (kind === "source_unverified" && onConfirmSource) {
    // The value isn't attested by a system of record — let the user consciously
    // confirm against source; the mount re-executes with confirm_source: true.
    cta = (
      <Button
        type="button"
        variant="primary"
        size="sm"
        onClick={onConfirmSource}
        disabled={busy}
        aria-busy={busy || undefined}
        aria-label={t`Conferma e procedi`}
      >
        <ShieldQuestion className="h-3 w-3" aria-hidden strokeWidth={2} />
        {t`Conferma e procedi`}
      </Button>
    );
  }
  // source_contradicted has NO CTA — a system of record attests a different
  // value, so it is a correctness stop, not a user-overridable risk.

  return (
    <div
      role="alert"
      className={cn(
        "flex flex-wrap items-start gap-2.5 rounded-md border border-hairline bg-white/50 px-3 py-2.5",
        className,
      )}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-ando-orange" aria-hidden strokeWidth={1.75} />
      <div className="min-w-0 flex-1">
        <p className="font-medium text-[12.5px] text-ink-1">{title}</p>
        {message ? (
          <p className="mt-0.5 break-words text-[11.5px] text-ink-3 leading-snug">{message}</p>
        ) : null}
        {sourceValues && sourceValues.length > 0 ? (
          <p className="mt-1 text-[11.5px] text-ink-2">
            {t`Nei tuoi dati ufficiali`}:{" "}
            <span className="font-medium text-ink-1 tabular-nums">
              {sourceValues.join(", ")}
            </span>
          </p>
        ) : null}
      </div>
      {cta ? <div className="ml-auto shrink-0">{cta}</div> : null}
    </div>
  );
};
ActionErrorRecovery.displayName = "ActionErrorRecovery";
